import { Loader2 } from 'lucide-react'
import { lazy, Suspense } from 'react'
import type { RouteObject } from 'react-router-dom'
import { ExperienceLabRoute } from '@/components/auth/ExperienceLabRoute'
import { ProtectedRoute } from '@/components/auth/ProtectedRoute'
import { SuperAdminRoute } from '@/components/auth/SuperAdminRoute'
import { AppLayout } from '@/components/layout/AppLayout'
import { GuidedBuilderRoute } from '@/features/guided-builder/GuidedBuilderRoute'
import { AcceptInvitationPage } from '@/pages/AcceptInvitationPage'
import { ConnectBlueconicPage } from '@/pages/ConnectBlueconicPage'
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage'
// Eagerly-loaded public auth pages — included in the main bundle so
// login/signup/password-reset render instantly without chunk loading delay.
import { LoginPage } from '@/pages/LoginPage'
import { RegisterPage } from '@/pages/RegisterPage'
import { ResetPasswordPage } from '@/pages/ResetPasswordPage'
import { ShopifyAuthCallbackPage } from '@/pages/ShopifyAuthCallbackPage'
import { VerifyEmailPage } from '@/pages/VerifyEmailPage'
import type { User } from '@/stores/auth'
import type { RouteHandle } from './routeHandles.types'
import { trackerBreadcrumbs } from './trackerBreadcrumbs'

// Wraps React.lazy() with retry + reload logic to handle stale chunks after deploys.
// When Vite builds new hashed filenames, users with cached HTML will fail to load old chunks.
// This retries the import, and if all retries fail, reloads the page to fetch fresh HTML.
function lazyWithRetry<T extends React.ComponentType>(
  importFn: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    const reloadKey = `chunk-reload-${window.location.pathname}`

    try {
      return await importFn()
    } catch (error) {
      // If we haven't already tried reloading for this path, do a hard reload
      // to get fresh HTML with the correct chunk URLs
      if (!sessionStorage.getItem(reloadKey)) {
        sessionStorage.setItem(reloadKey, '1')
        window.location.reload()
        // Return a never-resolving promise to prevent rendering while reloading
        return new Promise(() => {
          // Intentionally unresolved while the browser reloads.
        })
      }

      // We already reloaded once and it still failed — clear the flag and throw
      sessionStorage.removeItem(reloadKey)
      throw error
    }
  })
}

// Lazy-loaded page components for code splitting (authenticated pages only)
const OnboardingPage = lazyWithRetry(() =>
  import('@/pages/OnboardingPage').then((m) => ({ default: m.OnboardingPage }))
)
const DashboardPage = lazyWithRetry(() =>
  import('@/pages/DashboardPage').then((m) => ({ default: m.DashboardPage }))
)
const ProductsPage = lazyWithRetry(() =>
  import('@/pages/ProductsPage').then((m) => ({ default: m.ProductsPage }))
)
const KnowledgePage = lazyWithRetry(() =>
  import('@/pages/KnowledgePage').then((m) => ({ default: m.KnowledgePage }))
)
const ExperienceLabDashboardPage = lazyWithRetry(
  () => import('@/pages/ExperienceLabDashboardPage')
)
const ExperienceLabPage = lazyWithRetry(
  () => import('@/pages/ExperienceLabPage')
)
const SettingsPage = lazyWithRetry(() =>
  import('@/pages/SettingsPage').then((m) => ({ default: m.SettingsPage }))
)
const ProfilePage = lazyWithRetry(() =>
  import('@/pages/ProfilePage').then((m) => ({ default: m.ProfilePage }))
)
const OrganizationsPage = lazyWithRetry(() =>
  import('@/pages/OrganizationsPage').then((m) => ({
    default: m.OrganizationsPage,
  }))
)
const UsersPage = lazyWithRetry(() =>
  import('@/pages/UsersPage').then((m) => ({ default: m.UsersPage }))
)
const UserDetailPage = lazyWithRetry(() =>
  import('@/pages/UserDetailPage').then((m) => ({ default: m.UserDetailPage }))
)
const ChatBuilderPage = lazyWithRetry(() =>
  import('@/pages/ChatBuilderPage').then((m) => ({
    default: m.ChatBuilderPage,
  }))
)
const GuidedBuilderPage = lazyWithRetry(() =>
  import('@/features/guided-builder/GuidedBuilderPage').then((m) => ({
    default: m.GuidedBuilderPage,
  }))
)
const WorkspacesPage = lazyWithRetry(() =>
  import('@/pages/WorkspacesPage').then((m) => ({ default: m.WorkspacesPage }))
)
const FeatureFlagsPage = lazyWithRetry(() =>
  import('@/pages/FeatureFlagsPage').then((m) => ({
    default: m.FeatureFlagsPage,
  }))
)
const FeatureFlagDetailPage = lazyWithRetry(() =>
  import('@/pages/FeatureFlagDetailPage').then((m) => ({
    default: m.FeatureFlagDetailPage,
  }))
)
const PlatformInsightsPage = lazyWithRetry(() =>
  import('@/pages/PlatformInsightsPage').then((m) => ({
    default: m.PlatformInsightsPage,
  }))
)
const IntegrationsPage = lazyWithRetry(() =>
  import('@/pages/IntegrationsPage').then((m) => ({
    default: m.IntegrationsPage,
  }))
)
const ProfilePropertiesPage = lazyWithRetry(() =>
  import('@/pages/ProfilePropertiesPage').then((m) => ({
    default: m.ProfilePropertiesPage,
  }))
)
const LeadsPage = lazyWithRetry(() =>
  import('@/pages/LeadsPage').then((m) => ({ default: m.LeadsPage }))
)
const TrackerPage = lazyWithRetry(() =>
  import('@/pages/TrackerPage').then((m) => ({ default: m.TrackerPage }))
)
const InternalDocsPage = lazyWithRetry(() => import('@/pages/InternalDocsPage'))

// Minimal loading fallback for lazy-loaded pages
// Centers within the flex-1 content area of AppLayout
function PageLoader() {
  return (
    <div className="flex h-full items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  )
}

// Wrapper component that adds Suspense to lazy-loaded pages
// This is a proper React component that only renders when the route is matched
function LazyPage({
  component: Component,
}: {
  component: React.ComponentType
}) {
  return (
    <Suspense fallback={<PageLoader />}>
      <Component />
    </Suspense>
  )
}

// Route configuration
export const routes: RouteObject[] = [
  // Public routes (outside AppLayout) — eagerly loaded for instant render
  {
    path: '/login',
    element: <LoginPage />,
  },
  {
    path: '/register',
    element: <RegisterPage />,
  },
  {
    path: '/onboarding',
    element: (
      <ProtectedRoute requireEmailVerification={true}>
        <LazyPage component={OnboardingPage} />
      </ProtectedRoute>
    ),
  },
  {
    path: '/forgot-password',
    element: <ForgotPasswordPage />,
  },
  {
    path: '/verify-email',
    element: <VerifyEmailPage />,
  },
  {
    path: '/reset-password',
    element: <ResetPasswordPage />,
  },
  {
    path: '/accept-invitation',
    element: <AcceptInvitationPage />,
  },
  {
    path: '/connect/blueconic',
    element: <ConnectBlueconicPage />,
  },
  {
    path: '/auth/shopify',
    element: <ShopifyAuthCallbackPage />,
  },

  // Protected routes (inside AppLayout)
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <LazyPage component={DashboardPage} />,
        handle: {
          crumb: [
            { label: 'Shopping Assistant', path: '/dashboard' },
            { label: 'Dashboard' },
          ],
        } satisfies RouteHandle,
      },
      {
        path: 'dashboard',
        element: <LazyPage component={DashboardPage} />,
        handle: {
          crumb: [
            { label: 'Shopping Assistant', path: '/dashboard' },
            { label: 'Dashboard' },
          ],
        } satisfies RouteHandle,
      },
      {
        path: 'products',
        element: <LazyPage component={ProductsPage} />,
        handle: {
          crumb: [{ label: 'Sources' }, { label: 'Products' }],
        } satisfies RouteHandle,
        // Children render null because ProductsPage renders both views;
        // this keeps the import hook mounted across a switch.
        children: [
          { index: true, element: null },
          { path: ':collectionId', element: null },
        ],
      },
      {
        path: 'knowledge',
        element: <LazyPage component={KnowledgePage} />,
        handle: {
          crumb: [{ label: 'Sources' }, { label: 'Knowledge' }],
        } satisfies RouteHandle,
      },
      {
        path: 'experience-lab',
        element: (
          <ExperienceLabRoute>
            <LazyPage component={ExperienceLabDashboardPage} />
          </ExperienceLabRoute>
        ),
        handle: {
          crumb: [
            { label: 'Experience Lab', path: '/experience-lab' },
            { label: 'Dashboard' },
          ],
        } satisfies RouteHandle,
      },
      {
        path: 'experience-lab/new',
        element: (
          <ExperienceLabRoute>
            <LazyPage component={ExperienceLabPage} />
          </ExperienceLabRoute>
        ),
        handle: { fullBleed: true } satisfies RouteHandle,
      },
      {
        path: 'experience-lab/legacy/:legacyDemoId',
        element: (
          <ExperienceLabRoute>
            <LazyPage component={ExperienceLabPage} />
          </ExperienceLabRoute>
        ),
        handle: { fullBleed: true } satisfies RouteHandle,
      },
      {
        path: 'experience-lab/:experienceId',
        element: (
          <ExperienceLabRoute>
            <LazyPage component={ExperienceLabPage} />
          </ExperienceLabRoute>
        ),
        handle: { fullBleed: true } satisfies RouteHandle,
      },
      {
        path: 'settings',
        element: <LazyPage component={SettingsPage} />,
        handle: { crumb: [{ label: 'Settings' }] } satisfies RouteHandle,
      },
      {
        path: 'profile',
        element: <LazyPage component={ProfilePage} />,
        handle: { crumb: [{ label: 'Profile' }] } satisfies RouteHandle,
      },
      {
        path: 'organizations',
        element: (
          <ProtectedRoute
            roles={['owner', 'super admin', 'organization admin']}
          >
            <LazyPage component={OrganizationsPage} />
          </ProtectedRoute>
        ),
        handle: {
          crumb: [{ label: 'Organizations' }],
        } satisfies RouteHandle,
      },
      {
        path: 'users',
        element: (
          <ProtectedRoute roles={['owner', 'super admin']}>
            <LazyPage component={UsersPage} />
          </ProtectedRoute>
        ),
        handle: { crumb: [{ label: 'Users' }] } satisfies RouteHandle,
      },
      {
        path: 'users/:id',
        element: (
          <ProtectedRoute roles={['owner', 'super admin']}>
            <LazyPage component={UserDetailPage} />
          </ProtectedRoute>
        ),
        handle: { crumb: [{ label: 'Users' }] } satisfies RouteHandle,
      },
      {
        path: 'chat-builder/guided',
        element: (
          <ProtectedRoute>
            <GuidedBuilderRoute>
              <LazyPage component={GuidedBuilderPage} />
            </GuidedBuilderRoute>
          </ProtectedRoute>
        ),
        handle: { fullBleed: true } satisfies RouteHandle,
      },
      {
        path: 'chat-builder/:id/guided',
        element: (
          <ProtectedRoute>
            <GuidedBuilderRoute>
              <LazyPage component={GuidedBuilderPage} />
            </GuidedBuilderRoute>
          </ProtectedRoute>
        ),
        handle: { fullBleed: true } satisfies RouteHandle,
      },
      {
        path: 'chat-builder/:id/guided/:stage',
        element: (
          <ProtectedRoute>
            <GuidedBuilderRoute>
              <LazyPage component={GuidedBuilderPage} />
            </GuidedBuilderRoute>
          </ProtectedRoute>
        ),
        handle: { fullBleed: true } satisfies RouteHandle,
      },
      {
        path: 'chat-builder',
        element: (
          <ProtectedRoute>
            <LazyPage component={ChatBuilderPage} />
          </ProtectedRoute>
        ),
        handle: { fullBleed: true } satisfies RouteHandle,
      },
      {
        path: 'chat-builder/:id',
        element: (
          <ProtectedRoute>
            <LazyPage component={ChatBuilderPage} />
          </ProtectedRoute>
        ),
        handle: { fullBleed: true } satisfies RouteHandle,
      },
      {
        // View-tab deep links: /chat-builder/:id/editor, /conversations, /analytics
        // Analytics sub-tab lives in ?tab=overview|insights|ask-ai (see ChatBuilder).
        path: 'chat-builder/:id/:view',
        element: (
          <ProtectedRoute>
            <LazyPage component={ChatBuilderPage} />
          </ProtectedRoute>
        ),
        handle: { fullBleed: true } satisfies RouteHandle,
      },
      {
        path: 'workspaces',
        element: (
          <ProtectedRoute>
            <LazyPage component={WorkspacesPage} />
          </ProtectedRoute>
        ),
        handle: { crumb: [{ label: 'Workspaces' }] } satisfies RouteHandle,
      },
      {
        path: 'feature-flags',
        element: (
          <ProtectedRoute roles={['super admin']}>
            <LazyPage component={FeatureFlagsPage} />
          </ProtectedRoute>
        ),
        handle: {
          crumb: [{ label: 'Feature Flags' }],
        } satisfies RouteHandle,
      },
      {
        path: 'feature-flags/:id',
        element: (
          <ProtectedRoute roles={['super admin']}>
            <LazyPage component={FeatureFlagDetailPage} />
          </ProtectedRoute>
        ),
        handle: {
          crumb: [{ label: 'Feature Flags' }],
        } satisfies RouteHandle,
      },
      {
        path: 'platform-insights',
        element: (
          <ProtectedRoute>
            <SuperAdminRoute>
              <LazyPage component={PlatformInsightsPage} />
            </SuperAdminRoute>
          </ProtectedRoute>
        ),
        handle: {
          crumb: [
            { label: 'Shopping Assistant', path: '/dashboard' },
            { label: 'Platform Insights' },
          ],
        } satisfies RouteHandle,
      },
      {
        path: 'integrations',
        element: (
          <ProtectedRoute>
            <LazyPage component={IntegrationsPage} />
          </ProtectedRoute>
        ),
        handle: {
          crumb: [
            { label: 'Shopping Assistant', path: '/dashboard' },
            { label: 'Integrations' },
          ],
        } satisfies RouteHandle,
      },
      {
        path: 'profile-properties',
        element: (
          <ProtectedRoute requiresCdpConnection>
            <LazyPage component={ProfilePropertiesPage} />
          </ProtectedRoute>
        ),
        handle: {
          crumb: [
            { label: 'Shopping Assistant', path: '/dashboard' },
            { label: 'Profile Properties' },
          ],
        } satisfies RouteHandle,
      },
      {
        path: 'leads',
        element: (
          <ProtectedRoute>
            <LazyPage component={LeadsPage} />
          </ProtectedRoute>
        ),
        handle: {
          crumb: [
            { label: 'Shopping Assistant', path: '/dashboard' },
            { label: 'Leads' },
          ],
        } satisfies RouteHandle,
      },
      {
        // Splat: the package's nested routes (/performance, /history, /schedules, /run/:id) resolve under this basename.
        path: 'tracker/*',
        element: (
          <ProtectedRoute>
            <LazyPage component={TrackerPage} />
          </ProtectedRoute>
        ),
        handle: {
          crumb: trackerBreadcrumbs,
        } satisfies RouteHandle,
      },
      // Local-dev only: the internal docs viewer is never registered in a
      // deployed build, so the route does not exist in staging/prod.
      ...(import.meta.env.DEV
        ? [
            {
              path: 'internal-docs',
              element: <LazyPage component={InternalDocsPage} />,
              handle: {
                crumb: [{ label: 'Internal Docs' }],
                containedScroll: true,
              } satisfies RouteHandle,
            },
            {
              path: 'internal-docs/*',
              element: <LazyPage component={InternalDocsPage} />,
              handle: {
                crumb: [{ label: 'Internal Docs' }],
                containedScroll: true,
              } satisfies RouteHandle,
            },
          ]
        : []),
    ],
  },
]
// Route paths for type-safe navigation
export const routePaths = {
  // Public routes
  login: '/login',
  register: '/register',
  onboarding: '/onboarding',
  forgotPassword: '/forgot-password',
  resetPassword: '/reset-password',
  verifyEmail: '/verify-email',

  // Semi-public (requires auth to complete, but accessible without it)
  connectBlueconic: '/connect/blueconic',

  // Protected routes
  dashboard: '/dashboard',
  tracker: '/tracker',
  products: '/products',
  knowledge: '/knowledge',
  experienceLab: '/experience-lab',
  settings: '/settings',
  profile: '/profile',
  organizations: '/organizations',
  users: '/users',
  chatBuilder: '/chat-builder',
  workspaces: '/workspaces',
  featureFlags: '/feature-flags',
  platformInsights: '/platform-insights',
  integrations: '/integrations',
  profileProperties: '/profile-properties',
  leads: '/leads',
} as const

// Type for route paths
export type RoutePath = (typeof routePaths)[keyof typeof routePaths]

export function getDefaultRouteForUser(
  user?: Pick<User, 'defaultProduct'>
): RoutePath {
  return user?.defaultProduct === 'aeo_tracker'
    ? routePaths.tracker
    : routePaths.dashboard
}

// Navigation helper functions
export const navigationHelpers = {
  isPublicRoute: (path: string): boolean => {
    const publicRoutes = [
      routePaths.login,
      routePaths.register,
      routePaths.forgotPassword,
      routePaths.resetPassword,
      routePaths.verifyEmail,
    ] as string[]
    return publicRoutes.includes(path)
  },

  isProtectedRoute: (path: string): boolean => {
    return !navigationHelpers.isPublicRoute(path) && path !== '/'
  },

  getDefaultRouteForUser,
}

// Export route groups for easier management
export const routeGroups = {
  public: [
    routePaths.login,
    routePaths.register,
    routePaths.forgotPassword,
    routePaths.resetPassword,
    routePaths.verifyEmail,
  ],

  protected: [
    routePaths.dashboard,
    routePaths.tracker,
    routePaths.onboarding,
    routePaths.chatBuilder,
    routePaths.organizations,
    routePaths.users,
    routePaths.products,
    routePaths.knowledge,
    routePaths.settings,
    routePaths.profile,
    routePaths.workspaces,
    routePaths.featureFlags,
    routePaths.integrations,
    routePaths.profileProperties,
    routePaths.leads,
  ],

  admin: [routePaths.users, routePaths.organizations, routePaths.featureFlags],

  manager: [routePaths.organizations, routePaths.products],
}
